import { Box, Divider, Stack, Typography, SxProps, Theme } from "@mui/material";
import { useHighlightedFont } from "src/hooks/useHighlightedFont";
import { TableCardEntry } from "src/components/cards/senec-tile/dataTypes";
import React from "react";
import { useEntryColor } from "src/components/cards/senec-tile/table-card-content/tableCardUtils";
import { emptyDash } from "src/components/constants";

export const TableEntryLabel = ({
    data,
    highlightLabel,
    applyColor = true,
}: {
    data: TableCardEntry;
    highlightLabel?: boolean;
    applyColor?: boolean;
}) => {
    const { getHighlightedFont } = useHighlightedFont();
    const { getTextColor, getDefaultColor } = useEntryColor();

    return (
        <Typography
            variant={getHighlightedFont(highlightLabel)}
            sx={{
                wordBreak: "break-word",
                ...(applyColor ? { color: `${getTextColor(data)} !important` } : { color: `${getDefaultColor()} !important` }),
            }}
        >
            {data.label}
        </Typography>
    );
};

export const TableEntryValue = ({ data, highlightLabel }: { data: TableCardEntry; highlightLabel?: boolean }): JSX.Element => {
    const { getHighlightedFont } = useHighlightedFont();
    const { textColorIsDefined, getTextColor } = useEntryColor();
    const { value } = data;

    return (
        <Stack
            sx={{
                width: "100%",
            }}
        >
            {value === undefined || typeof value === "string" ? (
                <Typography
                    variant={getHighlightedFont(!highlightLabel)}
                    sx={{
                        wordBreak: "break-word",
                        ...(textColorIsDefined(data) ? { color: `${getTextColor(data)} !important` } : {}),
                    }}
                >
                    {value ?? emptyDash}
                </Typography>
            ) : (
                value
            )}
        </Stack>
    );
};

export const TableEntryIcon = ({
    data,
    hasPlaceholderSpacing = false,
}: {
    data: TableCardEntry;
    hasPlaceholderSpacing?: boolean;
}) => {
    const { getIconColor } = useEntryColor();
    const iconSize = "24px";
    if (!data.endAdornment && !hasPlaceholderSpacing) return null;

    if (!data.endAdornment && hasPlaceholderSpacing)
        return (
            <Box>
                <Box sx={{ width: iconSize, height: iconSize }} />
            </Box>
        );

    return React.createElement(data.endAdornment!.icon, {
        width: iconSize,
        height: iconSize,
        sx: { color: `${getIconColor(data)} !important` },
    });
};

type TableGroupDividerProps = { orientation?: "horizontal" | "vertical"; sx?: SxProps<Theme> | undefined };

export const TableGroupDivider = ({ orientation = "horizontal", sx }: TableGroupDividerProps) => {
    return (
        <Stack alignContent={"center"} sx={sx}>
            <Divider data-testid={"group-divider"} sx={{ borderColor: "neutralGray30.main" }} orientation={orientation} />
        </Stack>
    );
};
